/**
 * Mirrors: api/internal/httpapi/switches.go and reports.FrameworkSwitches (D25).
 *
 *   GET /frameworks/switches   the switch state of every switchable framework
 *
 * The overview, the history and every fleet row carry the same map as
 * `framework_switches`. Three states, and the client never infers one of them
 * from a missing score:
 *
 *   on       switched on. A missing score is then "not scored" or "not
 *            collected", never "off".
 *   off      the operator's choice. Rendered as exactly "Switched off" with
 *            where to switch it on — no score, no "not collected", no "not
 *            measured", and never hatched (a hatch means not measured).
 *   unknown  the setting could not be read. Shown as unknown, never as off.
 *
 * The sp-ms365 rule set is always on and has no entry.
 */
import { get } from "@/lib/api";
import { tenantPath, tenantScopeKey } from "./tenantScope";

export type SwitchState = "on" | "off" | "unknown";

export const SWITCHABLE_FRAMEWORKS = ["ms_baseline", "secure_score", "ai_bp"] as const;
export type SwitchableFramework = (typeof SWITCHABLE_FRAMEWORKS)[number];

export type FrameworkSwitches = Record<SwitchableFramework, SwitchState>;

/** Every switch unknown: what a missing or unreadable answer means. */
export const UNKNOWN_SWITCHES: FrameworkSwitches = {
  ms_baseline: "unknown",
  secure_score: "unknown",
  ai_bp: "unknown",
};

function asState(v: unknown): SwitchState {
  return v === "on" || v === "off" ? v : "unknown";
}

/**
 * Normalise a `framework_switches` map from the wire. A missing map or entry
 * is unknown — never off: "off" is a claim about the operator's choice that
 * only the server can make.
 */
export function normaliseSwitches(raw: unknown): FrameworkSwitches {
  const m = (raw !== null && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    ms_baseline: asState(m.ms_baseline),
    secure_score: asState(m.secure_score),
    ai_bp: asState(m.ai_bp),
  };
}

export const switchKeys = {
  all: () => ["framework-switches", tenantScopeKey()] as const,
};

export async function fetchFrameworkSwitches(): Promise<FrameworkSwitches> {
  const raw = await get<{ framework_switches?: unknown }>(tenantPath("/frameworks/switches"));
  return normaliseSwitches(raw.framework_switches);
}
